/**
 * Wire format for the multi-language sabaq/surah endpoints on
 * api.deenlearning.in:3006 (see docs/API_ENDPOINTS.md).
 *
 * Three things about the contract drive everything in this file:
 *
 *  1. Languages are a *suffix* on the field name (`sabaqNameHindi`), not a
 *     prefix, and the unsuffixed field (`sabaqName`) holds the default text
 *     that the sabaq list and the mobile app read.
 *  2. There is one shared set of sub-sabaq rows for all languages. Each row
 *     carries every language on it; the rows are not per-language lists.
 *  3. Languages are independent. Every key is written on every request, and a
 *     language the admin left blank is sent as an empty string -- never filled
 *     in from another language. The unsuffixed field tracks the first tab.
 *
 * Read and write use different names for the audio/reference fields, so each
 * field below is declared once with both spellings.
 */

export const LANGUAGES = [
  {
    key: "transliteration",
    label: "Transliteration",
    suffix: "Transliteration",
    rtl: false,
  },
  { key: "english", label: "English", suffix: "English", rtl: false },
  { key: "hindi", label: "Hindi", suffix: "Hindi", rtl: false },
  { key: "urdu", label: "Urdu", suffix: "Urdu", rtl: true },
  { key: "gujarati", label: "Gujarati", suffix: "Gujarati", rtl: false },
];

/** The tab that opens first. */
export const DEFAULT_LANG = LANGUAGES[0].key;

/** The unsuffixed field plus one per language. */
const SUFFIXES = ["", ...LANGUAGES.map((lang) => lang.suffix)];

export const getLanguage = (key) =>
  LANGUAGES.find((lang) => lang.key === key) || LANGUAGES[0];

export const suffixOf = (key) => getLanguage(key).suffix;

/** Text direction for the given language tab. */
export const dirOf = (key) => (getLanguage(key).rtl ? "rtl" : "ltr");

// State is keyed by the *write* name so building a payload is a direct copy;
// the response mappers below translate the read names back into these.
const SABAQ_TEXT = {
  sabaqName: "sabaqName",
  sabaqDesc: "sabaqDesc",
  sabaqDescReference: "descReference",
};

const SABAQ_AUDIO = {
  sabaqNameAudio: "nameAudioPath",
  sabaqDescAudio: "descAudioPath",
};

const SUB_TEXT = {
  subSabaqTitle: "subSabaqTitle",
  subSabaqDesc: "subSabaqDesc",
  subSabaqTarjama: "tarjama",
  subSabaqDescReference: "descReference",
};

const SUB_AUDIO = {
  subSabaqTitleAudio: "titleAudioPath",
  subSabaqDescAudio: "descAudioPath",
  subSabaqTarjamaAudio: "tarjamaAudioPath",
  subSabaqDescReferenceAudio: "descReferenceAudioPath",
};

/** A newly picked file, as opposed to a stored URL string or an empty slot. */
export const isNewFile = (value) =>
  typeof value === "object" && value !== null && typeof value.name === "string";

// Empty values serialise to undefined and are dropped from the JSON, so a
// language key is simply absent until someone authors that language.
const readValue = (source, key) => (source && source[key]) || "";

/**
 * The unsuffixed field and the first tab are two names for the same text.
 *
 * Nothing else is shared between languages: a tab left blank is written as an
 * empty string, never filled in from another language.
 */
const BASE_SUFFIX = LANGUAGES[0].suffix;

/** The tab whose fields the form marks required. */
export const DEFAULT_LANG_LABEL = LANGUAGES[0].label;

/**
 * True when a field that the default tab requires was left blank.
 *
 * Only the open tab's inputs are mounted, so a blank required field is
 * invisible to `form.checkValidity()` while another language is showing. Submit
 * handlers read the state instead and send the admin back to the default tab.
 */
export const isDefaultLangBlank = (source, write) =>
  !String((source && source[write + BASE_SUFFIX]) || "").trim();

const blankFields = (fields, extra) => {
  const state = { ...extra };
  SUFFIXES.forEach((suffix) => {
    Object.keys(fields).forEach((write) => {
      state[write + suffix] = "";
    });
  });
  return state;
};

/**
 * Tie the unsuffixed field to the first tab when reading a record back.
 *
 * Existing rows carry their content unsuffixed only, so it seeds that one tab
 * -- otherwise editing such a record would open on an empty form and saving it
 * would write the text away. The other tabs stay blank until authored.
 */
const alias = (target, write) => {
  target[write + BASE_SUFFIX] = target[write + BASE_SUFFIX] || target[write];
  target[write] = target[write + BASE_SUFFIX];
};

export const emptySubSabaq = () => ({
  ...blankFields({ ...SUB_TEXT, ...SUB_AUDIO }),
  subSabaqId: "",
  subSabaqNo: "",
  subSabaqImgVdo: "",
  isJumma: false,
});

export const emptySabaq = () => ({
  ...blankFields({ ...SABAQ_TEXT, ...SABAQ_AUDIO }),
  sabaqId: "",
  sabaqNo: "",
  orderNo: "",
  flag: "",
  sabaqImage: "",
  subSabaq: [emptySubSabaq()],
});

/**
 * `GET /subSabaqList` -> form state.
 *
 * Keeps the stored media URLs in the same slots the file inputs write to: a
 * string means "already on the server", a File means "replace it". Existing
 * records have content on the unsuffixed field only, so it seeds the first tab
 * -- otherwise editing one would open on an empty form. The remaining tabs stay
 * blank until someone authors them.
 */
export const mapSabaqResponse = (data) => {
  const state = emptySabaq();
  if (!data) return state;

  state.sabaqId = data.sabaqId || data.SabaqId || "";
  state.sabaqNo = data.sabaqNo === 0 || data.sabaqNo ? data.sabaqNo : "";
  state.orderNo = data.orderNo === 0 || data.orderNo ? data.orderNo : "";
  state.flag = data.flag || "";
  state.sabaqImage = data.sabaqImagePath || "";

  SUFFIXES.forEach((suffix) => {
    Object.entries(SABAQ_TEXT).forEach(([write, read]) => {
      state[write + suffix] = readValue(data, read + suffix);
    });
    Object.entries(SABAQ_AUDIO).forEach(([write, read]) => {
      state[write + suffix] = readValue(data, read + suffix);
    });
  });

  // The base slots also answer to their legacy aliases.
  state.sabaqDescReference =
    state.sabaqDescReference || readValue(data, "sabaqDescReference");
  state.sabaqNameAudio = state.sabaqNameAudio || readValue(data, "sabaqAudio");
  state.sabaqDescAudio =
    state.sabaqDescAudio || readValue(data, "sabaqDescAudio");

  Object.keys({ ...SABAQ_TEXT, ...SABAQ_AUDIO }).forEach((write) =>
    alias(state, write)
  );

  const rows = Array.isArray(data.subSabaqs) ? data.subSabaqs : [];
  state.subSabaq = rows.length
    ? rows.map((row, index) => {
        const mapped = emptySubSabaq();
        mapped.subSabaqId = row.subSabaqId || "";
        mapped.subSabaqNo = row.subSabaqNo || index + 1;
        mapped.subSabaqImgVdo = row.imagePath || "";
        mapped.isJumma = !!row.isJumma;

        SUFFIXES.forEach((suffix) => {
          Object.entries(SUB_TEXT).forEach(([write, read]) => {
            mapped[write + suffix] = readValue(row, read + suffix);
          });
          Object.entries(SUB_AUDIO).forEach(([write, read]) => {
            mapped[write + suffix] = readValue(row, read + suffix);
          });
        });
        Object.keys({ ...SUB_TEXT, ...SUB_AUDIO }).forEach((write) =>
          alias(mapped, write)
        );
        return mapped;
      })
    : [emptySubSabaq()];

  return state;
};

/**
 * Write one text field to the base slot and all five languages.
 *
 * Every key goes out on every write. A language the admin left blank is sent as
 * an empty string -- it is never filled in from another language.
 */
const appendText = (fd, source, write) => {
  fd.append(write, source[write + BASE_SUFFIX] || "");
  LANGUAGES.forEach(({ suffix }) => {
    fd.append(write + suffix, source[write + suffix] || "");
  });
};

/**
 * Write one audio field. Each file uploads once, to the tab it was picked on;
 * the base slot tracks the first tab. Leaving a slot out keeps whatever is
 * already stored there.
 */
const appendAudio = (fd, source, write) => {
  LANGUAGES.forEach(({ suffix }) => {
    const value = source[write + suffix];
    if (isNewFile(value)) fd.append(write + suffix, value);
  });
  if (isNewFile(source[write + BASE_SUFFIX]))
    fd.append(write, source[write + BASE_SUFFIX]);
};

/**
 * Form state -> `POST /addSabaq` or `PUT /updateSabaq` body.
 *
 * Sub-sabaq files go in flat at the top level; each row claims the next file
 * from a slot by carrying a marker on the matching key. Rows are read in array
 * order, so the files are appended in that same order.
 */
export const buildSabaqFormData = ({
  form,
  categoryId,
  userId,
  isEdit,
  sabaqNo,
  extra = {},
}) => {
  const fd = new FormData();
  const marker = isEdit ? "updated" : "file name";

  fd.append("categoryId", categoryId);
  if (userId || userId === 0) fd.append("userId", userId);

  if (isEdit) {
    fd.append("sabaqId", form.sabaqId);
  } else if (sabaqNo || sabaqNo === 0) {
    fd.append("sabaqNo", sabaqNo);
  }
  // The admin sets the ordering on both create and update. It is preserved
  // server-side when omitted, so a blank box is left out rather than sent empty.
  if (form.orderNo || form.orderNo === 0) fd.append("orderNo", form.orderNo);
  if (form.flag) fd.append("flag", form.flag);
  Object.entries(extra).forEach(([key, value]) => fd.append(key, value));

  // Text is written every time: an omitted field is stored as empty, not kept.
  Object.keys(SABAQ_TEXT).forEach((write) => appendText(fd, form, write));
  Object.keys(SABAQ_AUDIO).forEach((write) => appendAudio(fd, form, write));
  if (isNewFile(form.sabaqImage)) fd.append("sabaqImage", form.sabaqImage);

  const rows = Array.isArray(form.subSabaq) ? form.subSabaq : [];
  const pendingFiles = [];

  const subSabaq = rows.map((row, index) => {
    const entry = { subSabaqNo: index + 1, isJumma: !!row.isJumma };
    // A row with no id is inserted; a row left out entirely is deleted.
    if (isEdit && row.subSabaqId) entry.subSabaqId = row.subSabaqId;

    Object.keys(SUB_TEXT).forEach((write) => {
      entry[write] = row[write + BASE_SUFFIX] || "";
      LANGUAGES.forEach(({ suffix }) => {
        entry[write + suffix] = row[write + suffix] || "";
      });
    });

    Object.keys(SUB_AUDIO).forEach((write) => {
      LANGUAGES.forEach(({ suffix }) => {
        if (!isNewFile(row[write + suffix])) return;
        entry[write + suffix] = marker;
        pendingFiles.push([write + suffix, row[write + suffix]]);
      });
      if (isNewFile(row[write + BASE_SUFFIX])) {
        entry[write] = marker;
        pendingFiles.push([write, row[write + BASE_SUFFIX]]);
      }
    });

    if (isNewFile(row.subSabaqImgVdo)) {
      entry.subSabaqImgVdo = marker;
      pendingFiles.push(["subSabaqImgVdo", row.subSabaqImgVdo]);
    }
    return entry;
  });

  // The server eval()s this string, so it has to be built by JSON.stringify --
  // never by interpolating the text by hand.
  fd.append("subSabaq", JSON.stringify(subSabaq));
  pendingFiles.forEach(([slot, file]) => fd.append(slot, file));

  return fd;
};

// ---------------------------------------------------------------------------
// Hifz-e-Surah. The surah title takes every language; the ayah body is
// single-language by design and has no suffixed variants.
// ---------------------------------------------------------------------------

export const emptyAyat = () => ({
  ayatId: "",
  ayatNo: "",
  ayat: "",
  ayahAudio: "",
  waqf1: "",
  waqf2: "",
  waqf3: "",
});

export const emptySurah = () => ({
  ...blankFields({ sabaqName: "", sabaqNameAudio: "" }),
  sabaqId: "",
  surahNo: "",
  orderNo: "",
  ayatDetails: [emptyAyat()],
});

/** `GET /hifzeSurah/ayahList` -> form state. */
export const mapSurahResponse = (data) => {
  const state = emptySurah();
  if (!data) return state;

  state.sabaqId = data.sabaqId || data.SabaqId || "";
  state.surahNo = data.surahNo === 0 || data.surahNo ? data.surahNo : "";
  state.orderNo = data.orderNo === 0 || data.orderNo ? data.orderNo : "";

  SUFFIXES.forEach((suffix) => {
    state["sabaqName" + suffix] = readValue(data, "sabaqName" + suffix);
    state["sabaqNameAudio" + suffix] =
      readValue(data, "nameAudioPath" + suffix) ||
      readValue(data, "sabaqAudio" + suffix);
  });
  alias(state, "sabaqName");
  alias(state, "sabaqNameAudio");

  const ayahs = Array.isArray(data.ayatDetails) ? data.ayatDetails : [];
  state.ayatDetails = ayahs.length
    ? ayahs.map((ayah, index) => ({
        ayatId: ayah.ayatId || "",
        ayatNo: ayah.ayatNo || index + 1,
        ayat: ayah.ayat || "",
        ayahAudio: ayah.ayatAudioPath || "",
        waqf1: ayah.waqf1 || "",
        waqf2: ayah.waqf2 || "",
        waqf3: ayah.waqf3 || "",
      }))
    : [emptyAyat()];

  return state;
};

/** Form state -> `POST /hifzeSurah/addSurah` or `PUT /hifzeSurah/updateSurah`. */
export const buildSurahFormData = ({
  form,
  categoryId,
  userId,
  isEdit,
  surahNo,
}) => {
  const fd = new FormData();
  const marker = isEdit ? "updated" : "file name";

  fd.append("categoryId", categoryId);
  if (userId || userId === 0) fd.append("userId", userId);

  if (isEdit) {
    fd.append("surahId", form.sabaqId);
  } else if (surahNo || surahNo === 0) {
    fd.append("surahNo", surahNo);
  }
  if (form.orderNo || form.orderNo === 0) fd.append("orderNo", form.orderNo);

  // The base title and its audio keep the surah* names; the suffixed variants
  // ride on sabaqName<Lang> instead.
  fd.append("surahName", form["sabaqName" + BASE_SUFFIX] || "");
  LANGUAGES.forEach(({ suffix }) => {
    fd.append("sabaqName" + suffix, form["sabaqName" + suffix] || "");
  });

  LANGUAGES.forEach(({ suffix }) => {
    const audio = form["sabaqNameAudio" + suffix];
    if (isNewFile(audio)) fd.append("sabaqNameAudio" + suffix, audio);
  });
  if (isNewFile(form["sabaqNameAudio" + BASE_SUFFIX]))
    fd.append("surahNameAudio", form["sabaqNameAudio" + BASE_SUFFIX]);

  const rows = Array.isArray(form.ayatDetails) ? form.ayatDetails : [];
  const pendingFiles = [];

  const ayaahs = rows.map((row, index) => {
    const entry = {
      ayatNo: index + 1,
      ayat: row.ayat || "",
      waqf1: row.waqf1 || "",
      waqf2: row.waqf2 || "",
      waqf3: row.waqf3 || "",
    };
    if (isEdit && row.ayatId) entry.ayatId = row.ayatId;
    if (isNewFile(row.ayahAudio)) {
      entry.ayahAudio = marker;
      pendingFiles.push(row.ayahAudio);
    }
    return entry;
  });

  fd.append("ayaahs", JSON.stringify(ayaahs));
  pendingFiles.forEach((file) => fd.append("ayahAudio", file));

  return fd;
};
